import React, { useCallback, useState, useContext, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { Controller, Control, FieldErrors } from "react-hook-form";
import styles from "./SelectDropdown.module.scss";
import block from "bem-css-modules";
import { IFieldDropdown } from "./SelectDropdown.types";
import { sanitizeContent as s } from "../../../../util/functions";
import { AppContext } from "../../../../util/context";
import { workerCode } from "../../../../workers/filterWorker";

const b = block(styles);

const localizedContent = {
  "en-EN": {
    loadingMessage: "Loading, please wait...",
    noOptionsMessage: "No matching results found",
  },
  "de-DE": {
    loadingMessage: "Laden, bitte warten...",
    noOptionsMessage: "Keine passenden Ergebnisse gefunden",
  },
  "fr-FR": {
    loadingMessage: "Chargement, veuillez patienter...",
    noOptionsMessage: "Aucun résultat correspondant trouvé",
  },
};

const CustomOption = (props: any) => {
  const { data, innerRef, innerProps, isFocused } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: "10px",
        backgroundColor: isFocused ? "#f0f0f0" : "transparent",
        cursor: "pointer",
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: data.label }} />
    </div>
  );
};

const CustomSingleValue = (props: any) => {
  const { data } = props;
  return <div dangerouslySetInnerHTML={{ __html: data.label }} />;
};

const createWorker = () => {
  const blob = new Blob([workerCode], { type: 'application/javascript' });
  const workerUrl = URL.createObjectURL(blob);
  return new Worker(workerUrl);
};

interface VirtualizedAsyncSelectProps {
  fieldData: IFieldDropdown;
  isManualInput: boolean;
  onStateChangeManualInput: (newState: boolean) => void;
  control: Control;
  errors: FieldErrors;
}

const VirtualizedAsyncSelect: React.FC<VirtualizedAsyncSelectProps> = ({ fieldData, isManualInput, onStateChangeManualInput, control, errors }) => {
  const {
    title,
    label,
    placeholder,
    options,
    validationText,
    settings: [{ required }],
  } = fieldData;

  const { settings: { locale } }: { settings: { locale: keyof typeof localizedContent } } = useContext(AppContext);
  
  const workerRef = useRef<Worker | null>(null);
  const callbackRef = useRef<((opts: any[]) => void) | null>(null);

  const [loadingMessage, setLoadingMessage] = useState(localizedContent["en-EN"].loadingMessage);
  const [noOptionsMessage, setNoOptionsMessage] = useState(localizedContent["en-EN"].noOptionsMessage);
  const [allOptions, setAllOptions] = useState<any[]>(options || []);

  useEffect(() => {
    if (locale && localizedContent[locale]) {
      setLoadingMessage(localizedContent[locale].loadingMessage);
      setNoOptionsMessage(localizedContent[locale].noOptionsMessage);
    }
  }, [locale]);

  useEffect(() => {
    if (options && options.length > 0) {
      setAllOptions(options);
    }
  }, [options]);

  useEffect(() => {
    workerRef.current = createWorker();
    workerRef.current.onmessage = (event) => {
      const { results, done } = event.data;

      if (!callbackRef.current) return;
      if (done) {
        callbackRef.current(results);
      }
    };

    return () => workerRef.current?.terminate();
  }, []);

  const loadOptions = useCallback(
    (inputValue: string, callback: (opts: any[]) => void) => {
      if (inputValue.length < 3) {
        callback([]);
        return;
      }

      if (!workerRef.current) {
        callback([]);
        return;
      }

      callbackRef.current = callback;
      workerRef.current.postMessage({ options: allOptions, query: inputValue });
    },
    [allOptions]
  );

  return (
    <div className={b()}>
      {title && (
        <h3 className={b("title")} dangerouslySetInnerHTML={{ __html: s(title) }} />
      )}

      <Controller
        name={label}
        control={control}
        rules={{ required: isManualInput ? false : required }}
        render={({ field }) => {
          const selectedOption = allOptions?.find(
            (option) => option.value === field.value || option.value?.id_ === field.value?.id_
          );

          useEffect(() => {
            if (isManualInput) {
              field.onChange(null);
            }
          }, [field, isManualInput]);

          return (
            <AsyncSelect
              cacheOptions
              defaultOptions={allOptions.slice(0, 300)}
              loadOptions={loadOptions}
              components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
              placeholder={placeholder}
              className={b("select")}
              classNamePrefix="react-select"
              onChange={(val: any) => {
                field.onChange(val?.value);
                if (val) {
                  onStateChangeManualInput(false);
                }
              }}
              value={selectedOption || null}
              loadingMessage={() => loadingMessage}
              noOptionsMessage={() => noOptionsMessage}
            />
          );
        }}
      />

      {errors[label] && (
        <div className={b("error")} dangerouslySetInnerHTML={{ __html: s(validationText) }} />
      )}
    </div>
  );
};

export default VirtualizedAsyncSelect;
